import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Clock, Zap, AlertTriangle, CheckCircle, ArrowRight, Shield, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import NationalitySelect from "../components/NationalitySelect";
import { getNationalityCurrency } from "../data/nationalities";
import { useCurrency } from "../context/CurrencyContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [nationality, setNationality] = useState(searchParams.get("nationality") || "");
  const [destination, setDestination] = useState(searchParams.get("destination") || "");
  const [visaType, setVisaType] = useState("tourist");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [visaRule, setVisaRule] = useState(null);
  const { convert, currency, setCurrency } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/countries`).then(r => setCountries(r.data));
  }, []);

  const handleNationalityChange = (val) => {
    setNationality(val);
    const autoCurr = getNationalityCurrency(val);
    if (autoCurr) setCurrency(autoCurr);
  };

  useEffect(() => { if (destination && nationality) handleSearch(); }, [destination]);

  const handleSearch = async () => {
    if (!nationality || !destination) return;
    setLoading(true);
    try {
      const [pricingRes, rulesRes] = await Promise.all([
        axios.get(`${API}/pricing/dynamic`, { params: { nationality, destination, visa_type: visaType } }),
        axios.get(`${API}/visa-rules`, { params: { nationality, destination } }),
      ]);
      setResult(pricingRes.data);
      setVisaRule(rulesRes.data);
    } catch {} finally { setLoading(false); }
  };

  const icons = { standard: Clock, express: Zap, urgent: AlertTriangle };
  const labels = { standard: "Standard", express: "Express", urgent: "Urgent" };
  const colors = { standard: "border-slate-200", express: "border-blue-400 ring-1 ring-blue-100", urgent: "border-orange-300" };

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Home</button>
        <div className="text-center mb-10">
          <h1 className="heading-font text-3xl md:text-4xl font-bold text-slate-900 mb-2">Transparent Visa Pricing</h1>
          <p className="text-sm text-slate-500">No hidden fees. See the full breakdown before you pay.</p>
        </div>

        {/* Search */}
        <div className="card-elevated p-6 mb-10">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div><label className="text-xs text-slate-500 mb-1 block">Nationality</label>
              <NationalitySelect value={nationality} onValueChange={handleNationalityChange} /></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Destination</label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger data-testid="pricing-destination" className="w-full h-11 border-slate-200 rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-60">
                  {countries.map(c => <SelectItem key={c.country_code} value={c.country_code}><span className="flex items-center gap-2">{c.flag} {c.country_name}</span></SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Visa Type</label>
              <Select value={visaType} onValueChange={setVisaType}>
                <SelectTrigger className="w-full h-11 border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="tourist">Tourist</SelectItem><SelectItem value="business">Business</SelectItem><SelectItem value="student">Student</SelectItem>
                </SelectContent></Select></div>
            <div><label className="text-xs text-slate-500 mb-1 block">Currency</label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="w-full h-11 border-slate-200 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200 max-h-60">
                  {["USD", "INR", "GBP", "EUR", "AED", "CAD", "AUD", "SGD", "JPY", "THB", "MYR", "BRL"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent></Select></div>
          </div>
          <button onClick={handleSearch} disabled={loading || !nationality || !destination} data-testid="pricing-search-btn"
            className="w-full h-11 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all disabled:opacity-40">
            {loading ? "Calculating..." : "Get Pricing"}
          </button>
          {currency !== "USD" && (
            <p className="text-xs text-blue-500 mt-2 text-center flex items-center justify-center gap-1"><RefreshCw className="w-3 h-3" /> Prices shown in {currency} (auto-detected)</p>
          )}
        </div>

        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="pricing-results">
            {/* Visa Method Card */}
            {visaRule && (
              <div className={`glass-card p-5 mb-8 border-l-4 ${
                visaRule.method === "visa_free" ? "border-l-emerald-500 bg-emerald-50/30" :
                visaRule.method === "evisa" ? "border-l-blue-500 bg-blue-50/30" :
                visaRule.method === "assisted" ? "border-l-amber-500 bg-amber-50/30" :
                "border-l-red-400 bg-red-50/30"
              }`} data-testid="visa-method-card">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className={`text-xs font-bold uppercase tracking-wider ${
                      visaRule.method === "visa_free" ? "text-emerald-600" :
                      visaRule.method === "evisa" ? "text-blue-600" :
                      visaRule.method === "assisted" ? "text-amber-600" : "text-red-500"
                    }`}>{visaRule.method_label}</span>
                    <p className="text-sm text-slate-600 mt-0.5">{visaRule.method_description}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">Processing</p>
                    <p className="text-sm font-semibold text-slate-900">{visaRule.processing_time}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span className="text-slate-500">Difficulty: <strong className={visaRule.difficulty === "None" ? "text-emerald-600" : visaRule.difficulty === "Easy" ? "text-blue-600" : visaRule.difficulty === "Medium" ? "text-amber-600" : "text-red-500"}>{visaRule.difficulty}</strong></span>
                  <span className="text-slate-500">Steps: <strong>{visaRule.steps.length}</strong></span>
                </div>
                {/* Steps */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {visaRule.steps.map((s, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[11px] text-slate-600 bg-white/80 px-2.5 py-1 rounded-full border border-slate-200">
                      <span className="w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[9px] font-bold">{i+1}</span>{s}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Trust badge */}
            <div className="flex items-center justify-center gap-2 mb-8 text-emerald-600 bg-emerald-50 rounded-full px-4 py-2 w-fit mx-auto border border-emerald-100">
              <Shield className="w-4 h-4" /><span className="text-xs font-semibold">No hidden charges. All-inclusive pricing.</span>
            </div>

            <h2 className="heading-font text-xl font-semibold text-slate-900 mb-6 text-center">
              {result.destination_name} — {visaType.charAt(0).toUpperCase() + visaType.slice(1)} Visa
            </h2>

            {/* Options */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {(result.all_options || []).map((opt) => {
                const Icon = icons[opt.processing_type] || Clock;
                const isExpress = opt.processing_type === "express";
                return (
                  <div key={opt.processing_type} className={`card-elevated rounded-2xl p-6 relative ${colors[opt.processing_type]} ${isExpress ? "bg-blue-50/30" : ""}`}
                    data-testid={`pricing-option-${opt.processing_type}`}>
                    {isExpress && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-blue-600 text-white px-3 py-1 rounded-full">RECOMMENDED</span>}
                    <div className="flex items-center gap-2 mb-4">
                      <Icon className={`w-5 h-5 ${isExpress ? "text-blue-600" : "text-slate-400"}`} />
                      <span className="text-sm font-semibold text-slate-800">{labels[opt.processing_type]}</span>
                    </div>
                    <div className="text-3xl font-bold text-slate-900 heading-font mb-1">{convert(opt.total)}</div>
                    <p className="text-xs text-slate-400 mb-4">{opt.processing_time_days} business days</p>

                    {/* Breakdown */}
                    <div className="space-y-1.5 mb-4 pb-4 border-b border-slate-100">
                      {opt.breakdown.map(b => (
                        <div key={b.label} className="flex justify-between text-xs">
                          <span className="text-slate-400">{b.label}</span><span className="text-slate-700">{convert(b.amount)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-xs font-semibold pt-1">
                        <span className="text-slate-700">Total</span><span className="text-blue-600">{convert(opt.total)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-emerald-600 mb-4">
                      <CheckCircle className="w-3.5 h-3.5" />{opt.approval_rate}% approval rate
                    </div>

                    {result.pay_after_approval && opt.processing_type === "standard" && (
                      <div className="text-xs text-blue-600 bg-blue-50 rounded-lg px-3 py-2 mb-3 border border-blue-100">
                        Pay only {convert(result.deposit_amount || opt.total * 0.2)} deposit now. Rest after approval.
                      </div>
                    )}

                    <button onClick={() => navigate(`/eligibility?destination=${destination}&nationality=${nationality}&type=${visaType}&processing=${opt.processing_type}`)}
                      className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all ${isExpress ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
                      Apply Now
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
